import React, { createContext, useContext, useMemo } from "react";
import { shouldUseIonicComponents, getCurrentPlatform } from "../../utils/platformUtils";

/**
 * Platform Context
 *
 * Provides platform configuration throughout the app.
 * Allows forcing a specific platform for testing/debugging.
 */

const PlatformContext = createContext(null);

/**
 * PlatformProvider Component
 *
 * Wraps the application to provide platform-aware context.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.forcePlatform - Force a specific platform ('ios', 'android', 'web') for testing
 */
export const PlatformProvider = ({ children, forcePlatform = null }) => {
  const value = useMemo(() => {
    // If forcePlatform is set, use it to determine useIonic
    const useIonic = forcePlatform
      ? forcePlatform === "ios" || forcePlatform === "android"
      : shouldUseIonicComponents();

    const platform = forcePlatform || getCurrentPlatform();

    return {
      // Current platform ('ios', 'android', 'web')
      platform,
      // Whether to use Ionic components
      useIonic,
      // Whether platform is being forced (for testing)
      isForced: !!forcePlatform,
      // Platform-specific configuration
      config: {
        // Animation duration multiplier (mobile animations are faster)
        animationDuration: useIonic ? 0.3 : 0.2,
        // Default border radius
        borderRadius: useIonic ? "10px" : "4px",
        // Touch target size (mobile needs larger targets)
        minTouchTarget: useIonic ? "44px" : "36px"
      }
    };
  }, [forcePlatform]);

  return (
    <PlatformContext.Provider value={value}>
      {children}
    </PlatformContext.Provider>
  );
};

/**
 * Hook to access platform context
 *
 * @returns {Object} Platform context value
 * @throws {Error} If used outside of PlatformProvider
 */
export const usePlatformContext = () => {
  const context = useContext(PlatformContext);

  if (context === null) {
    // Return default values if used outside provider (graceful degradation)
    return {
      platform: getCurrentPlatform(),
      useIonic: shouldUseIonicComponents(),
      isForced: false,
      config: {
        animationDuration: 0.2,
        borderRadius: "4px",
        minTouchTarget: "36px"
      }
    };
  }

  return context;
};

/**
 * Hook that returns just the useIonic boolean
 * Convenient shorthand for components that only need to know which library to use
 *
 * @returns {boolean} True if Ionic should be used
 */
export const useIsIonic = () => {
  const { useIonic } = usePlatformContext();
  return useIonic;
};

/**
 * Hook that returns platform-specific config values
 *
 * @returns {Object} Platform configuration
 */
export const usePlatformConfig = () => {
  const { config } = usePlatformContext();
  return config;
};

export default PlatformProvider;
